import React, { useEffect, useState, useRef } from "react";
import Card from "components/card";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import VerificationBadgeService from "services/verificationbadge.service";
import Loader from "../../../assets/img/loader.gif";
import Toast from "components/toaster";
import { BsChevronDown } from "react-icons/bs";
import { FiSearch } from "react-icons/fi";
import GooglePagination from "components/pagination/GooglePagination";

const columnHelper = createColumnHelper();

// Module-level tabCache for persistent data across component mounts/unmounts
const tabCache = {
  "verification-badge-request": {
    data: null,
    firstLoad: true,
  },
};

const VerificationBadgeRequest = () => {
  const dropdownRef = useRef();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isViewLoading, setIsViewLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const getVerificationBadgeData = async (showLoader = true) => {
    if (showLoader) {
      setIsLoading(true);
    }
    try {
      const body = {
        pagination: {
          page: currentPage,
          pageSize: pageSize,
          sort: { createdAt: -1 }
        },
        searchtext: searchText
      };
      const response = await VerificationBadgeService.getVerificationBadgeRequests(body);
      
      // Handle the response structure from the API
      const responseData = response.data;
      let dataArray = [];
      let totalCount = 0;
      let totalPagesFromAPI = 1;
      
      if (responseData && responseData.data) {
        // Data is directly an array in response.data.data
        if (Array.isArray(responseData.data)) {
          dataArray = responseData.data;
        }
        
        // Get pagination info from API response
        totalCount = responseData.totalCount || 0;
        totalPagesFromAPI = Math.ceil(totalCount / pageSize);
      }
      
      // Update cache
      tabCache["verification-badge-request"].data = {
        data: dataArray,
        totalCount: totalCount,
        totalPages: totalPagesFromAPI,
      };
      
      // Set firstLoad to false after successful fetch
      if (tabCache["verification-badge-request"].firstLoad) {
        tabCache["verification-badge-request"].firstLoad = false;
      }
      
      setData(dataArray);
      setTotalCount(totalCount);
      setTotalPages(totalPagesFromAPI);
    } catch (error) {
      console.error("Error fetching verification badge data:", error);
      console.error("Error response:", error.response);
      Toast("error", error.response?.data?.message || "Failed to fetch verification badge data");
    } finally {
      if (showLoader) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    // Check cache first
    const cache = tabCache["verification-badge-request"];
    
    // If cached data exists, show it instantly
    if (cache.data) {
      setData(cache.data.data || []);
      setTotalCount(cache.data.totalCount || 0);
      setTotalPages(cache.data.totalPages || 1);
    }
    
    // Always fetch fresh data in background
    // Show loader only on firstLoad (when no cache exists yet)
    const shouldShowLoader = cache.firstLoad && !cache.data;
    getVerificationBadgeData(shouldShowLoader);
  }, [currentPage, pageSize, searchText]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo(0, 0); // Scroll to top when changing pages
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleApprove = async (accountId) => {
    setIsViewLoading(true);
    try {
      // First, approve the request
      const approveBody = {
        account_id: accountId,
        status_id: 1
      };
      const approveResponse = await VerificationBadgeService.approveRejectVerificationBadge(approveBody);
      
      // Then, assign the verification badge (if not already assigned)
      try {
        const manageBody = {
          account_id: accountId,
          assign: true
        };
        await VerificationBadgeService.manageVerificationBadge(manageBody);
      } catch (manageError) {
        // If badge is already assigned, that's fine - just continue
        if (manageError.response?.status !== 400 || 
            !manageError.response?.data?.message?.includes("already assigned")) {
          // Re-throw if it's a different error
          throw manageError;
        }
      }
      
      Toast("success", approveResponse.data.message || "Request approved and badge assigned successfully");
      getVerificationBadgeData(false);
      
      // Dispatch custom event to notify other components (like Account table) to refresh
      window.dispatchEvent(new CustomEvent('verificationBadgeApproved', {
        detail: { accountId }
      }));
    } catch (error) {
      Toast(
        "error",
        error.response?.data?.message || "Failed to approve request"
      );
    } finally {
      setActiveDropdown(null);
      setIsViewLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      Toast("error", "Please provide a rejection reason");
      return;
    }

    setIsViewLoading(true);
    try {
      const body = {
        account_id: selectedRequest.account_id || selectedRequest._id,
        status_id: 2,
        rejection_message: rejectionReason
      };
      const response = await VerificationBadgeService.approveRejectVerificationBadge(body);
      Toast("success", response.data.message || "Request rejected successfully");
      getVerificationBadgeData(false);
      setShowRejectModal(false);
      setRejectionReason("");
      setSelectedRequest(null);
    } catch (error) {
      Toast(
        "error",
        error.response?.data?.message || "Failed to reject request"
      );
    } finally {
      setActiveDropdown(null);
      setIsViewLoading(false);
    }
  };

  const openRejectModal = (request) => {
    setSelectedRequest(request);
    setShowRejectModal(true);
    setRejectionReason("");
  };

  const getStatusBadge = (status) => {
    // Map status_id to status text
    let statusText = "Pending";
    let statusColor = "bg-yellow-500";
    
    if (status === 1 || status === "approved" || status === "1") {
      statusText = "Approved";
      statusColor = "bg-green-500";
    } else if (status === 2 || status === "rejected" || status === "2") {
      statusText = "Rejected";
      statusColor = "bg-red-500";
    }
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white ${statusColor}`}>
        {statusText}
      </span>
    );
  };

  const columns = [
    columnHelper.accessor((row) => {
      const account = row.account && Array.isArray(row.account) ? row.account[0] : row.account;
      return account?.name || "N/A";
    }, {
      id: "userName",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Full Name</p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-white">
          {info.getValue() || "N/A"}
        </p>
      ),
    }),
    columnHelper.accessor((row) => {
      const account = row.account && Array.isArray(row.account) ? row.account[0] : row.account;
      return account?.email || null;
    }, {
      id: "userEmail",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Email</p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          {info.getValue() || "--"}
        </div>
      ),
    }),
    columnHelper.accessor((row) => {
      const account = row.account && Array.isArray(row.account) ? row.account[0] : row.account;
      return account?.phone || account?.new_phone || "N/A";
    }, {
      id: "userPhone",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Phone Number</p>
      ),
      cell: (info) => (
        <div className="flex items-center">
          {info.getValue() || "--"}
        </div>
      ),
    }),
    columnHelper.accessor((row) => {
      const account = row.account && Array.isArray(row.account) ? row.account[0] : row.account;
      return account?.account_type || "N/A";
    }, {
      id: "accountType",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Account Type</p>
      ),
      cell: (info) => (
        <p className="text-sm font-bold text-white">
          {info.getValue() ? info.getValue() : "-"}
        </p>
      ),
    }),
    columnHelper.accessor("createdAt", {
      id: "createdAt",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Created At</p>
      ),
      cell: (info) => {
        const date = new Date(info.getValue());
        return (
          <div className="flex items-center">
            {date.toLocaleDateString()} {date.toLocaleTimeString()}
          </div>
        );
      },
    }),
    columnHelper.accessor((row) => {
      // Convert status_id to status string to match livestream format
      const statusId = row.status_id;
      if (statusId === 1) return "approved";
      if (statusId === 2) return "rejected";
      return "pending";
    }, {
      id: "status",
      header: () => (
        <p className="text-sm font-bold uppercase text-white">Status</p>
      ),
      cell: (info) => {
        const rowId = info.row.original._id;
        const status = info.getValue();
        
        return (
          <div className="relative">
            <div
              className={`ms-center m-0 flex w-28 cursor-pointer items-center justify-between rounded-lg ${
                status === "approved" ? "bg-green-500/20" : 
                status === "rejected" ? "" : "bg-[#FF80001A]/10"
              } px-3 py-1.5 text-sm font-bold ${
                status === "approved" ? "text-green-500" :
                status === "rejected" ? "" : "text-[rgba(255,128,0,1)]"
              }`}
              style={status === "rejected" ? {
                backgroundColor: "rgba(255,37,37,0.1)",
                color: "#f06670d5"
              } : {}}
              onClick={() =>
                setActiveDropdown(activeDropdown === rowId ? null : rowId)
              }
            >
              <span>
                {status === "approved" ? "Approved" : 
                 status === "rejected" ? "Rejected" : "Pending"}
              </span>
              <BsChevronDown 
                className={`h-3 w-3 ${status === "approved" ? "text-green-500" : ""}`}
                style={status === "rejected" ? {
                  stroke: "#f06670d5",
                  strokeWidth: "1",
                  fill: "none"
                } : status === "pending" ? {
                  stroke: "rgba(255,128,0,1)",
                  strokeWidth: "1",
                  fill: "none"
                } : {
                  stroke: "currentColor",
                  strokeWidth: "1",
                  fill: "none"
                }}
              />
            </div>
            {activeDropdown === rowId && (
              <div
                className="absolute left-0 z-50 mt-1 w-28 rounded-md bg-[#0F1735] px-1 py-1"
                style={{ boxShadow: '0 0 10px 2px rgba(255, 255, 255, 0.15)' }}
                ref={dropdownRef}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="block w-full rounded-md px-2.5 py-1 text-center text-sm font-bold text-white transition-colors hover:bg-gray-500"
                  onClick={() => {
                    if (status !== "approved") {
                      handleApprove(info.row.original.account_id);
                    } else {
                      setActiveDropdown(null);
                    }
                  }}
                >
                  Approve
                </button>
                <div className="border-t border-gray-600 my-1 mx-2"></div>
                <button
                  className="block w-full rounded-md px-2.5 py-1 text-center text-sm font-bold text-white transition-colors hover:bg-gray-500"
                  onClick={() => {
                    if (status !== "rejected") {
                      openRejectModal(info.row.original);
                    } else {
                      setActiveDropdown(null);
                    }
                  }}
                >
                  Reject
                </button>
              </div>
            )}
          </div>
        );
      },
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    debugTable: false,
  });

  return (
    <div className="mt-3 grid h-full grid-cols-1">
      <div className="col-span-1 h-full w-full xl:col-span-1">
        <div className="flex justify-between gap-3 mb-4">
          <div className="mt-2 md:mt-4 flex h-fit justify-start">
            <div className="flex items-center rounded-full bg-navy-800 py-2 h-[40px] text-white xl:w-[170px]">
              <p className="pl-3 pr-2 text-xl">
                <FiSearch className="h-4 w-4 text-white" />
              </p>
              <input
                type="text"
                placeholder="Search..."
                className="block w-[90px] max-w-[90px] bg-navy-800 text-sm font-medium text-white outline-none placeholder:!text-white"
                value={searchText}
                onChange={handleSearch}
              />
            </div>
          </div>
        </div>
        <Card extra={"w-full h-fit pb-6 relative"}>
          <div className="mt-2 mx-2">
            <div className="overflow-x-auto !p-0 !m-0 !pr-0">
              <table className="table-fixed w-full min-w-[1200px] border-collapse !mr-0 !pr-0 !border-r-0" style={{ tableLayout: 'fixed', width: '100%' }}>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className="!border-px !border-gray-400">
                    {headerGroup.headers.map((header, index) => {
                      const isFirst = index === 0;
                      const isLast = index === headerGroup.headers.length - 1;
                      const isStatus = header.id === 'status';
                      const isPhone = header.id === 'userPhone';
                      
                      return (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          style={{ width: 'calc(100% / 6)' }}
                          className={`cursor-pointer border-b-[1px] border-gray-500/10 pb-2 pt-4 text-start ${isFirst ? 'pl-6' : isPhone ? 'pl-3' : 'pl-4'} ${isLast ? '' : 'pr-4'} ${isStatus ? 'pl-8' : ''}`}
                        >
                          <div className="items-center justify-between text-xs text-gray-200">
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {isLoading && tabCache["verification-badge-request"]?.firstLoad && !tabCache["verification-badge-request"]?.data ? (
                  <tr>
                    <td colSpan={columns.length} className="h-[600px] align-middle">
                      <div className="flex h-full items-center justify-center">
                        <img
                          src={Loader}
                          alt="Loading..."
                          className="h-16 w-16"
                        />
                      </div>
                    </td>
                  </tr>
                ) : table.getRowModel().rows?.length === 0 ? (
                  <tr>
                    <td colSpan={columns.length} className="py-4 text-center">
                      No data found
                    </td>
                  </tr>
                ) : (
                  table.getRowModel().rows.map((row, rowIndex) => {
                    const isLastRow = rowIndex === table.getRowModel().rows.length - 1;
                    return (
                      <tr key={row.id} className={isLastRow ? "" : "border-b-[1px] border-gray-500/10"}>
                        {row.getVisibleCells().map((cell, cellIndex) => {
                          const isFirst = cellIndex === 0;
                          const isLast = cellIndex === row.getVisibleCells().length - 1;
                          const isStatus = cell.column.id === 'status';
                          const isPhone = cell.column.id === 'userPhone';
                          return (
                            <td
                              key={cell.id}
                              className={`border-white/0 py-3 ${isFirst ? 'pl-6' : isPhone ? 'pl-3' : 'pl-4'} ${isLast ? '' : 'pr-4'} ${isStatus ? 'pl-8' : ''}`}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
            </div>
          </div>

          {/* Google-style Pagination */}
          {!isLoading && table.getRowModel().rows.length > 0 && totalPages > 1 && (
            <div className="mt-3 pt-3 px-6">
              <GooglePagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                className="w-full"
              />
            </div>
          )}
        </Card>
      </div>

      {/* Rejection Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-96 rounded-xl bg-navy-800 p-6 shadow-lg">
            <h2 className="mb-4 text-xl font-bold text-white">Reject Verification Badge Request</h2>
            <div className="mb-4">
              <label className="block text-sm font-medium text-white mb-2">
                Rejection Reason *
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-white bg-white"
                rows="4"
                placeholder="Please provide a reason for rejection..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason("");
                  setSelectedRequest(null);
                }}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                onClick={handleReject}
                disabled={isViewLoading}
              >
                {isViewLoading ? "Rejecting..." : "Reject Request"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VerificationBadgeRequest;

